// Pricing configuration
export const pricingConfig = {
  base: {
    monthly: 70,
    quarterly: 180,
    biyearly: 300,
    yearly: 480
  },
  bulkDiscount: {
    // 5% for 2 companies, 10% for 3 companies, etc., max 45%
    // Formula: (companyCount - 1) * 5%, capped at 45%
    maxDiscount: 45,
    discountPerCompany: 5
  },
  features: {
    'bank-integration': {
      monthly: 40,
      quarterly: 99,
      biyearly: 162,
      yearly: 240
    },
    'payroll': {
      monthly: 30,
      quarterly: 75,
      biyearly: 120,
      yearly: 180
    },
    'electronic-invoicing': {
      monthly: 30,
      quarterly: 75,
      biyearly: 120,
      yearly: 180
    },
    'invoice-processing': {
      monthly: 40,
      quarterly: 99,
      biyearly: 162,
      yearly: 240
    },
    'digital-signing-id-card': {
      monthly: 50,
      quarterly: 135,
      biyearly: 240,
      yearly: 420,
      baseAmount: 1000 // Base price is for 1000 signatures/month
    },
    'digital-signing-smart-id': {
      monthly: 90,
      quarterly: 240,
      biyearly: 420,
      yearly: 720,
      baseAmount: 550 // Base price is for 550 signatures/month
    },
    'erp-integration': {
      monthly: 40,
      quarterly: 120,
      biyearly: 240,
      yearly: 480
    },
    'premium-support': {
      monthly: 40,
      quarterly: 99,
      biyearly: 162,
      yearly: 240
    }
  },
  invoiceVolume: {
    '<50': { multiplier: 0.5, caption: '50' },
    '50-200': { multiplier: 0.75, caption: '200' },
    '200-500': { multiplier: 1, caption: '500' },
    '>500': { multiplier: 1.5, caption: '1500' },
    'dont-know': { multiplier: 1, caption: '1500' }
  },
  featureLabels: {
    'bank-integration': 'Direct bank integration',
    'payroll': 'Payroll',
    'electronic-invoicing': 'Electronic invoicing',
    'invoice-processing': 'Incoming invoice processing',
    'digital-signing-id-card': 'Signing with ID card',
    'digital-signing-smart-id': 'Signing with Smart-ID',
    'erp-integration': 'ERP integration',
    'premium-support': 'Premium support'
  },
  billingPeriodLabels: {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    biyearly: 'Bi-yearly',
    yearly: 'Yearly'
  },
  currency: 'EUR',
  currencyDecimals: 2,
  formatCurrency: 'en-US'
};

// Get invoice volume caption
export const getInvoiceVolumeCaption = (invoiceCount) => {
  const volumeConfig = pricingConfig.invoiceVolume[invoiceCount] || { caption: '1500' };
  return volumeConfig.caption;
};

// Get per-company invoice volume for per-company features
export const getPerCompanyInvoiceVolume = (totalInvoiceCount, companyCount) => {
  // Get the actual invoice count from the volume config
  const volumeConfig = pricingConfig.invoiceVolume[totalInvoiceCount] || { caption: '1500' };
  const totalInvoices = parseInt(volumeConfig.caption);
  
  // Calculate per-company invoice count
  const perCompanyInvoices = Math.ceil(totalInvoices / companyCount);
  
  // Map back to the closest volume tier
  if (perCompanyInvoices < 50) {
    return '<50';
  } else if (perCompanyInvoices <= 200) {
    return '50-200';
  } else if (perCompanyInvoices <= 500) {
    return '200-500';
  } else {
    return '>500';
  }
};

// Calculate volume-based pricing for invoice features
export const calculateInvoiceFeaturePrice = (invoiceCount, period, type) => {
  const basePrice = pricingConfig.features[type === 'customer' ? 'electronic-invoicing' : 'invoice-processing'][period];
  const volumeConfig = pricingConfig.invoiceVolume[invoiceCount] || { multiplier: 1, caption: '1500' };

  return Math.round(basePrice * volumeConfig.multiplier);
};

// Calculate volume-based pricing for ID Card feature
export const calculateIdCardFeaturePrice = (invoiceCount, period) => {
  const basePrice = pricingConfig.features['digital-signing-id-card'][period];
  const baseAmount = pricingConfig.features['digital-signing-id-card'].baseAmount;
  
  // Get the actual invoice count from the volume config
  const volumeConfig = pricingConfig.invoiceVolume[invoiceCount] || { caption: '1500' };
  const actualInvoiceCount = parseInt(volumeConfig.caption);
  
  // Use the higher of base invoices (1000) or actual invoice count
  const signatureCount = Math.max(baseAmount, actualInvoiceCount);
  
  // Calculate proportional pricing based on signature count
  const multiplier = signatureCount / baseAmount;
  
  return Math.round(basePrice * multiplier);
};

// Calculate volume-based pricing for Smart-ID feature
export const calculateSmartIdFeaturePrice = (invoiceCount, period) => {
  const basePrice = pricingConfig.features['digital-signing-smart-id'][period];
  const baseAmount = pricingConfig.features['digital-signing-smart-id'].baseAmount;
  
  // Get the actual invoice count from the volume config
  const volumeConfig = pricingConfig.invoiceVolume[invoiceCount] || { caption: '1500' };
  const actualInvoiceCount = parseInt(volumeConfig.caption);
  
  // Use the higher of base invoices (550) or actual invoice count
  const signatureCount = Math.max(baseAmount, actualInvoiceCount);
  
  // Calculate proportional pricing based on signature count
  const multiplier = signatureCount / baseAmount;
  
  return Math.round(basePrice * multiplier);
};

// Calculate premium support price based on selected per-company features
export const calculatePremiumSupportPrice = (data, billingPeriod, companyCount) => {
  const selectedFeatures = data.selectedFeatures || [];
  let premiumSupportPrice = pricingConfig.features['premium-support'][billingPeriod];

  if (selectedFeatures.includes('bank-integration')) {
    premiumSupportPrice += pricingConfig.features['bank-integration'][billingPeriod];
  }

  if (selectedFeatures.includes('payroll')) {
    premiumSupportPrice += pricingConfig.features['payroll'][billingPeriod];
  }

  if (selectedFeatures.includes('electronic-invoicing')) {
    if (data.customerInvoiceCount) {
      const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.customerInvoiceCount, companyCount);
      premiumSupportPrice += calculateInvoiceFeaturePrice(perCompanyInvoiceCount, billingPeriod, 'customer');
    } else {
      premiumSupportPrice += pricingConfig.features['electronic-invoicing'][billingPeriod];
    }
  }

  if (selectedFeatures.includes('invoice-processing')) {
    if (data.supplierInvoiceCount) {
      const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.supplierInvoiceCount, companyCount);
      premiumSupportPrice += calculateInvoiceFeaturePrice(perCompanyInvoiceCount, billingPeriod, 'supplier');
    } else {
      premiumSupportPrice += pricingConfig.features['invoice-processing'][billingPeriod];
    }
  }

  return premiumSupportPrice;
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat(pricingConfig.formatCurrency, {
    style: 'currency',
    currency: pricingConfig.currency,
    minimumFractionDigits: pricingConfig.currencyDecimals,
    maximumFractionDigits: pricingConfig.currencyDecimals
  }).format(amount);
};

// Calculate pricing based on wizard data
export const calculatePricing = (data, billingPeriod = 'yearly') => {
  const companyCount = data.selectedOption && ['1', '2', '3', '4', '5'].includes(data.selectedOption)
    ? parseInt(data.selectedOption)
    : parseInt(data.companyCount) || 1;

  let basePrice = pricingConfig.base[billingPeriod];
  let featuresBreakdown = [];
  let featuresPrice = 0;

  // Check if both Smart-ID and ID card are selected
  const hasSmartId = data.selectedFeatures.includes('digital-signing-smart-id');
  const hasIdCard = data.selectedFeatures.includes('digital-signing-id-card');
  const bothSigningSelected = hasSmartId && hasIdCard;

  // Add ERP integration if user selected "with existing system"
  if (data.usageType === 'with-existing') {
    const erpPrice = pricingConfig.features['erp-integration'][billingPeriod];
    featuresPrice += erpPrice;
    featuresBreakdown.push({
      feature: 'erp-integration',
      price: erpPrice,
      volumeBased: false,
      volumeCaption: data.existingSystem || 'existing system',
      isIncluded: false,
      isAutoIncluded: true
    });
  }

  // Add feature pricing with volume-based pricing for invoice features
  data.selectedFeatures.forEach(feature => {
    if (pricingConfig.features[feature]) {
      let featurePrice = pricingConfig.features[feature][billingPeriod];

      // Apply volume-based pricing for invoice features
      let volumeCaption = '';
      if (feature === 'electronic-invoicing' && data.customerInvoiceCount) {
        // For per-company features, divide total invoice count by company count
        const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.customerInvoiceCount, companyCount);
        featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, billingPeriod, 'customer');
        volumeCaption = getInvoiceVolumeCaption(perCompanyInvoiceCount);
      } else if (feature === 'invoice-processing' && data.supplierInvoiceCount) {
        // For per-company features, divide total invoice count by company count
        const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.supplierInvoiceCount, companyCount);
        featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, billingPeriod, 'supplier');
        volumeCaption = getInvoiceVolumeCaption(perCompanyInvoiceCount);
      } else if (feature === 'digital-signing-id-card' && data.supplierInvoiceCount) {
        if (bothSigningSelected) {
          // ID card is included for free when both are selected
          featurePrice = 0;
          // Use Smart-ID signature count for the caption
          const smartIdSignatureCount = Math.max(pricingConfig.features['digital-signing-smart-id'].baseAmount, parseInt(getInvoiceVolumeCaption(data.supplierInvoiceCount)));
          volumeCaption = smartIdSignatureCount.toString();
        } else {
          featurePrice = calculateIdCardFeaturePrice(data.supplierInvoiceCount, billingPeriod);
          volumeCaption = getInvoiceVolumeCaption(data.supplierInvoiceCount);
        }
      } else if (feature === 'digital-signing-smart-id' && data.supplierInvoiceCount) {
        featurePrice = calculateSmartIdFeaturePrice(data.supplierInvoiceCount, billingPeriod);
        volumeCaption = getInvoiceVolumeCaption(data.supplierInvoiceCount);
      }

      featuresPrice += featurePrice;
      featuresBreakdown.push({
        feature,
        price: featurePrice,
        volumeBased: (feature === 'electronic-invoicing' && data.customerInvoiceCount) ||
          ((feature === 'invoice-processing' || feature === 'digital-signing-id-card' || feature === 'digital-signing-smart-id') && data.supplierInvoiceCount),
        volumeCaption: volumeCaption,
        isIncluded: bothSigningSelected && feature === 'digital-signing-id-card',
        isAutoIncluded: false
      });
    }
  });

  // Add premium support after selected features so it appears after
  // related per-company feature lines in pricing and email breakdowns.
  if (data.supportType === 'premium-support' || data.usageType === 'with-existing') {
    const supportPrice = calculatePremiumSupportPrice(data, billingPeriod, companyCount);
    featuresPrice += supportPrice;
    featuresBreakdown.push({
      feature: 'premium-support',
      price: supportPrice,
      volumeBased: false,
      volumeCaption: '',
      isIncluded: false,
      isAutoIncluded: data.usageType === 'with-existing'
    });
  }

  // Separate per-company features from per-configuration features
  let perCompanyFeaturesPrice = 0;
  let perConfigurationFeaturesPrice = 0;
  
  // Identify signing features (per-configuration)
  const signingFeatures = ['digital-signing-id-card', 'digital-signing-smart-id'];
  
  featuresBreakdown.forEach(feature => {
    if (signingFeatures.includes(feature.feature)) {
      perConfigurationFeaturesPrice += feature.price;
    } else {
      perCompanyFeaturesPrice += feature.price;
    }
  });

  // Calculate subtotal for per-company features
  let perCompanySubtotal = basePrice + perCompanyFeaturesPrice;

  // Apply bulk discount for multiple companies (only on per-company features)
  let bulkDiscountPercentage = 0;
  let bulkDiscountAmount = 0;
  if (companyCount > 1) {
    bulkDiscountPercentage = Math.min((companyCount - 1) * pricingConfig.bulkDiscount.discountPerCompany, pricingConfig.bulkDiscount.maxDiscount);
    bulkDiscountAmount = (perCompanySubtotal * companyCount * bulkDiscountPercentage) / 100;
  }

  // Calculate final price: (per-company subtotal * companyCount - bulk discount) + per-configuration features
  let totalPrice = (perCompanySubtotal * companyCount) - bulkDiscountAmount + perConfigurationFeaturesPrice;

  return {
    basePrice: basePrice,
    featuresPrice: featuresPrice,
    subtotal: perCompanySubtotal + perConfigurationFeaturesPrice,
    perCompanySubtotal: perCompanySubtotal,
    perConfigurationFeaturesPrice: perConfigurationFeaturesPrice,
    companyCount: companyCount,
    bulkDiscountPercentage: bulkDiscountPercentage,
    bulkDiscountAmount: bulkDiscountAmount,
    featuresBreakdown: featuresBreakdown,
    totalPrice: totalPrice
  };
};

